import React, { useState, useEffect } from 'react';
import { Alert } from 'react-native';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut, sendPasswordResetEmail, User } from 'firebase/auth';
import { firebaseConfig } from './firebaseConfig';
import { NavigationContainer } from '@react-navigation/native';
import { Center, NativeBaseProvider, Text } from 'native-base';
import { createStackNavigator } from '@react-navigation/stack';
import AuthScreen from './src/components/AuthScreen';
import HomeScreen from './src/components/HomeScreen';
import ActivationScreen from './src/components/ActivationScreen';
import AutoLoginScreen from './src/components/AutoLoginScreen'; 
import { RootStackParamList } from './src/types';

import AsyncStorage from '@react-native-async-storage/async-storage';
import MinhaContaScreen from './src/components/MinhaContaScreen';
import DetalhesContaScreen from './src/components/DetalhesContaScreen';

import { doc, getDoc, updateDoc, getFirestore } from 'firebase/firestore';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setPersistence, browserLocalPersistence, signInWithEmailAndPassword } from 'firebase/auth';

// Inicializações
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const queryClient = new QueryClient();

const Stack = createStackNavigator<RootStackParamList>(); // Stack tipado!

const App = () => {
  const [user, setUser] = useState<User | null>(null);
  const [myEmail, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [activationCode, setActivationCode] = useState<string>('');
  const [codeInput, setCodeInput] = useState<string>('');
  const [isActivated, setIsActivated] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        try {
          await AsyncStorage.setItem('user_id', user.uid);
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            await updateDoc(userRef, { isActive: true });
            setIsActivated(true);
            setUser(user);
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error('Erro ao carregar o estado do usuário:', error);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleForgotPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, myEmail);
      Alert.alert('Verifique seu e-mail', 'Um link de redefinição de senha foi enviado para seu e-mail.');
      setError(null);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Ocorreu um erro inesperado.');
      }
    }
  };

  const handleSignIn = async (navigation: any) => {
    try {
      await setPersistence(auth, browserLocalPersistence);

      const userCredential = await signInWithEmailAndPassword(
        auth,
        myEmail.trim(),
        password
      );

      const user = userCredential.user;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        setUser(user);
        setIsActivated(true);
        setError(null);
        navigation.navigate('Home');
      } else {
        setError('Usuário não encontrado no banco de dados.');
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Ocorreu um erro inesperado.');
      }
    }
  };

  const handleSignOut = () => {
    signOut(auth).then(() => setUser(null)).catch(console.error);
  };

  if (loading) {
    // Exibe uma tela de carregamento enquanto verifica a autenticação
    return (
      <QueryClientProvider client={queryClient}>
        <NativeBaseProvider>
          <Center flex={1}>
            <Text>Carregando...</Text>
          </Center>
        </NativeBaseProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <NativeBaseProvider>
        <NavigationContainer>
          <Stack.Navigator initialRouteName={isActivated ? 'Home' : 'Auth'}>
            <Stack.Screen name="Auth" options={{ headerShown: false }}>
              {({ navigation }) => (
                <AuthScreen
                  handleSignIn={() => handleSignIn(navigation)}
                  handleForgotPassword={handleForgotPassword}
                  error={error}
                  setEmail={setEmail}
                  setPassword={setPassword}
                  myEmail={myEmail}
                  password={password}
                />
              )}
            </Stack.Screen>
            <Stack.Screen name="AutoLogin" options={{ headerShown: false }}>
              {({ navigation, route }) => (
                <AutoLoginScreen navigation={navigation} route={route} />
              )}
            </Stack.Screen>
            <Stack.Screen name="Activation" options={{ title: 'Ativação' }}>
              {() => (<ActivationScreen />)}
            </Stack.Screen>
            <Stack.Screen name="Home" options={{ title: 'Home', headerShown: false }}>
              {() => <HomeScreen onSignOut={handleSignOut} />}
            </Stack.Screen>
            <Stack.Screen name="MinhaConta" options={{ title: 'MinhaConta', headerShown: false }}>
              {() => <MinhaContaScreen onSignOut={handleSignOut} />}
            </Stack.Screen>
            <Stack.Screen name="DetalhesConta" options={{ title: 'DetalhesConta', headerShown: false }}>
              {() => <DetalhesContaScreen onSignOut={handleSignOut} />}
            </Stack.Screen>
          </Stack.Navigator>
        </NavigationContainer>
      </NativeBaseProvider>
    </QueryClientProvider>
  );
};

export default App;
